import AppContext from 'App/AppContext';
import { TFunction } from 'i18next';
import { getIdentityProviderName, hasEntraDirectory } from 'models/identityProvider';
import { CustomerAccessStatus } from 'models/tenant';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetPartnerCustomerAccess } from 'services/Api/partnerService';
import { apiRequest } from 'services/Auth/authConfig';
import { navigateToExternalUrl } from 'utils/url';
import { getTenantWidgetTenantDirectory, getTenantWidgetTenantId, TenantWidgetTenant } from './tenantWidgetData';

const getAccessDeniedText = (t: TFunction<string[]>, status: CustomerAccessStatus): string => {
  if (status === CustomerAccessStatus.NoPaidLicenseAtPartner) {
    return t('partnerAdmin:AccessStatus.NoPaidLicenseAtPartner');
  }

  if (status === CustomerAccessStatus.NoLicenseAtCustomer) {
    return t('partnerAdmin:AccessStatus.NoLicenseAtCustomer');
  }

  return t('partnerAdmin:AccessStatus.Title');
};

//Drill-down for the tenant bar-chart widgets. Opens the clicked tenant in a new tab with a token from
//that tenant's own directory, so the directory/orgunitid pair passes the tenant-boundary check in the API
//(PrincipalAuthValidator). Partner customers are validated with the same access check as the
//'Login to customer' action before navigating.
export const useTenantDrillDown = (): ((
  tenant: TenantWidgetTenant,
  url: string,
  isPartner: boolean,
) => Promise<void>) => {
  const { t } = useTranslation(['partnerAdmin']);
  const appContext = useContext(AppContext);

  const drillDown = useCallback(
    async (tenant: TenantWidgetTenant, url: string, isPartner: boolean): Promise<void> => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const directory = getTenantWidgetTenantDirectory(tenant);

      if (!isPartner) {
        //org units in the current directory keep the current tenant's directory; org units bound to a
        //separate directory need a token from their own
        navigateToExternalUrl(url, directory ?? appContext.user.tenant, tenantId);

        return;
      }

      if (!directory) {
        appContext.showNotification(t('partnerAdmin:AccessStatus.NoDirectoryAtCustomer'), true);

        return;
      }

      //a real sign-in at the customer's directory (the Entra B2B guest route): only a customer with an Entra
      //directory has one; a Google or OpenID Connect customer is reached through the customer dashboard
      if (!hasEntraDirectory(directory)) {
        appContext.showNotification(
          t('partnerAdmin:AccessStatus.NoEntraDirectoryAtCustomer', {
            provider: getIdentityProviderName(directory.identityProviderType),
          }),
          true,
        );

        return;
      }

      try {
        appContext.showContentLoader();
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const status = await apiGetPartnerCustomerAccess(tenantId, accessToken);
        if (status !== CustomerAccessStatus.Allowed) {
          appContext.showNotification(getAccessDeniedText(t, status), true);

          return;
        }

        navigateToExternalUrl(url, directory, tenantId);
      } catch (err) {
        appContext.setError(err);
      } finally {
        appContext.hideContentLoader();
      }
    },
    [appContext, t],
  );

  return drillDown;
};
